<template>
  <v-container>
    <v-toolbar color="transparent" flat class="mb-3">
      <v-toolbar-title class="text-xl-h4 text-lg-h5 font-weight-bold">
       {{ $t('News.List.header') }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-dialog v-model="showEditor" max-width="700px" persistent>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" dark class="mb-2" v-on="on">{{$t('News.Button.add')}}</v-btn>
        </template>
        <NewsEditor 
          v-if="showEditor"
          :defaultNews="editedIndex >= 0 ? newsList[editedIndex] : null" 
          :formTitle="editedIndex >= 0 ? 'Modification' : 'Création'"
          @save="save"
          @cancel="close"
          :loading="saving"
        />
      </v-dialog>
    </v-toolbar>

    <v-row justify="space-around">
      <v-col cols="6">
        <SearchField 
          @search="value => search = value" 
          :help="$t('News.List.searchHint')"
        />
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="filteredNews"
            :items-per-page="$vuetify.breakpoint.lgAndDown ? 5 : 10"
            :footer-props="{'items-per-page-options': [5, 10, 20]}"
            sort-by="updatedAt"
            sort-desc
            class="pt-5"
          >
            <template v-slot:item.type="{ item }">
              <v-icon v-if="item.type === 'image'">image</v-icon>
              <v-icon v-else-if="item.type === 'image-cover'">photo_album</v-icon>
            </template>
            <template v-slot:item.preview="{ item }">
              <v-img :src="$_file_getNewsPath(item.imageName)" height="50px" aspect-ratio="3/2" contain></v-img>
            </template>
            <template v-slot:item.author="{ item }">
              <v-chip color="primary" text-color="white" v-if="item.author">
                <v-avatar>
                  <v-icon>account_circle</v-icon>
                </v-avatar>
                {{ item.author }}
              </v-chip>
            </template>
            <template v-slot:item.updatedAt="{ item }">
              {{ item.updatedAtFormatted }}
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ item.createdAtFormatted }}
            </template>
            <template v-slot:item.isActive="{ item }">
              <v-switch color="primary" v-model="item.isActive" @click.stop="switchState(item.id)"></v-switch>
            </template>
            <template v-slot:item.isPublic="{ item }">
              <v-icon v-if="item.isPublic">check</v-icon>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn fab small icon @click="editItem(item)" color="secondary">
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn fab small icon @click="deleteItem(item)" color="secondary">
                <v-icon small>delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  
  </v-container>
</template>

<script>
import NewsEditor from '@/components/News/NewsEditor'
import SearchField from '@/components/Utils/SearchField'
import { FileMixin, DateMixin } from '@/mixins/index'
import { matchTextOnObject } from '@/helpers/search'
import { NewsRepository } from '@portal/api'
export default {
  components: { NewsEditor, SearchField },
  mixins: [FileMixin, DateMixin],
  data: () => ({
    newsList: [],
    showEditor: false,
    search: '',
    editedIndex: -1,
    saving: false
  }),

  computed: {
    formattedNews () {
      return this.newsList.map(news => ({
        ...news,
        updatedAtFormatted: this.$_date_format(news.updatedAt),
        createdAtFormatted: this.$_date_format(news.createdAt),
      }))
    },
    filteredNews () {
      return this.formattedNews.filter(news => {
        return matchTextOnObject(this.search, news, ['title', 'description', 'author'])
      })
    },
    headers () {
      return [
        { sortable: false, value: 'type', width: '50px' },
        { sortable: false, value: 'preview', width: '125px' },
        { text: this.$t('News.Form.title'), align: 'left', value: 'title' },
        { text: this.$t('News.Form.author'), value: 'author' },
        { text: this.$t('Global.Common.updatedAt'), value: 'updatedAt' },
        { text: this.$t('Global.Common.createdAt'), value: 'createdAt' },
        { text: this.$t('News.Form.active'), value: 'isActive' },
        { text: this.$t('News.Form.public'), value: 'isPublic' },
        { sortable: false, value: 'actions', width: '150px' },
      ]
    }
  },

  watch: {
    showEditor (val) {
      val || this.close()
    }
  },

  created () {
    this.initialize()
  },

  methods: {
    async initialize () {
      this.newsList = await NewsRepository.fetchAll()
    },

    editItem (item) {
      this.editedIndex = this.newsList.findIndex(news => news.id === item.id)
      this.showEditor = true
    },

    async deleteItem (item) {
      if(confirm(this.$t('News.Message.delete'))) {

      await NewsRepository.delete(item.id)
      }
      const index = this.newsList.findIndex(el => el.id === item.id)
      this.newsList.splice(index, 1)
    },

    close () {
      this.showEditor = false
      setTimeout(() => {
        this.editedIndex = -1
      }, 300);
    },

    async save (newsData) {
      this.saving = true
      if (this.editedIndex > -1) {
        try {
          let updatedNews = await NewsRepository.update(newsData.id, newsData)
          if (newsData.file) {
            updatedNews = await NewsRepository.uploadImg(updatedNews.id, newsData.file)
          }
          this.newsList.splice(this.editedIndex, 1, updatedNews)
          this.close()
        } catch (error) {
          this.$store.dispatch('notify/error', this.$t('Message.Error.update'))
        }
      } else {
        try {
          let createdNews = await NewsRepository.create(newsData)
          if (newsData.file) {
            createdNews = await NewsRepository.uploadImg(createdNews.id, newsData.file)
          }
          this.newsList.push(createdNews)
          this.close()
        } catch (error) {
          this.$store.dispatch('notify/error', this.$t('Message.Error.create'))
        }
      }
      this.saving = false
    },

    async switchState (id) {
      const newsIndex = this.newsList.findIndex(news => news.id === id)
      const updatedNews = await NewsRepository.update(id, {isActive: !this.newsList[newsIndex].isActive})
      this.newsList.splice(newsIndex, 1, updatedNews)
    },

    formatDate(date) {
      return new Date(date).toLocaleString()
    }
  }
}
</script>